import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVault } from '@/lib/supabase/VaultContext';
import { spring } from '@/lib/motion/presets';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

export function TagSelector({ selectedTags, onChange }: TagSelectorProps) {
  const { tags } = useVault();
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update suggestions based on input
  useEffect(() => {
    if (!input.trim()) {
      // Show all tags that aren't already selected
      setSuggestions(
        tags
          .map((t) => t.name)
          .filter((name) => !selectedTags.some((st) => st.toLowerCase() === name.toLowerCase()))
      );
      return;
    }

    const query = input.toLowerCase();
    const filtered = tags
      .map((t) => t.name)
      .filter(
        (name) =>
          name.toLowerCase().includes(query) &&
          !selectedTags.some((st) => st.toLowerCase() === name.toLowerCase())
      );
    setSuggestions(filtered);
    setActiveIndex(0);
  }, [input, tags, selectedTags]);

  function addTag(tagName: string) {
    const cleanTag = tagName.trim();
    if (!cleanTag) return;

    // Avoid duplicates
    if (!selectedTags.some((t) => t.toLowerCase() === cleanTag.toLowerCase())) {
      onChange([...selectedTags, cleanTag]);
    }
    setInput('');
    setIsOpen(false);
    inputRef.current?.focus();
  }

  function removeTag(tagName: string) {
    onChange(selectedTags.filter((t) => t !== tagName));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && suggestions.length > 0) {
        addTag(suggestions[activeIndex]);
      } else if (input.trim()) {
        addTag(input);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setActiveIndex((prev) => (prev + 1) % Math.max(1, suggestions.length));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        setActiveIndex((prev) => (prev - 1 + suggestions.length) % Math.max(1, suggestions.length));
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && !input && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full space-y-2">
      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Tags</label>

      {/* Input container with pills inside */}
      <div className="flex w-full flex-wrap gap-1.5 rounded-md border border-line bg-surface p-2 focus-within:border-line-strong transition-colors">
        <AnimatePresence>
          {selectedTags.map((tag) => (
            <motion.span
              key={tag}
              layout
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={spring}
              className="inline-flex items-center gap-1 rounded bg-bg px-2 py-0.5 text-xs text-ink-muted border border-line"
            >
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:text-danger rounded-full p-0.5 transition-colors"
                aria-label={`Remove tag ${tag}`}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </motion.span>
          ))}
        </AnimatePresence>

        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? "Add tags..." : ""}
          className="flex-1 min-w-[80px] bg-transparent text-xs text-ink placeholder:text-ink-faint focus:outline-none"
        />
      </div>

      {/* Suggestions dropdown */}
      <AnimatePresence>
        {isOpen && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute z-30 w-full max-h-48 overflow-y-auto rounded-md border border-line bg-surface-raised p-1 shadow-accent-glow"
          >
            {suggestions.map((tag, idx) => (
              <button
                key={tag}
                type="button"
                onClick={() => addTag(tag)}
                className={`w-full text-left rounded px-2.5 py-1.5 text-xs transition-colors ${
                  idx === activeIndex
                    ? 'bg-bg text-ink font-medium'
                    : 'text-ink-muted hover:bg-bg/40 hover:text-ink'
                }`}
              >
                {tag}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
